/**
 * 修复画笔（补齐 registry.ts 早先登记的占位工具，复用 §2.11 内容识别填充引擎）。
 *
 * 笔画覆盖的区域当洞，套用与"内容识别填充"完全相同的 PatchMatch 引擎——
 * 修复画笔在 Photoshop 里就是"用画笔手动圈洞的内容识别填充"，没必要
 * 另写一套采样算法。笔画覆盖度直接当 applyFill 的调制蒙版用，
 * 笔刷边缘的软硬度（hardness）天然带出羽化，不需要额外裁剪。
 */
import { TILE } from '../engine/tile/key';
import { applyFill } from '../engine/fill/apply';
import { contentAwareFill, type ContentAwareFillOptions } from '../engine/fill/content-aware';
import type { StrokeBuffer } from '../engine/brush/buffer';
import { compositeDocument } from './composite';
import type { AppStore } from './store';

/**
 * 用 `buffer` 里累积的笔画覆盖度当洞，跑内容识别填充并合并进活动 raster 图层。
 * 一次调用 = 一条历史。空笔画（没有任何 stamp）或无活动图层时是无操作。
 */
export async function healStrokeDocument(
  store: AppStore,
  buffer: StrokeBuffer,
  options: ContentAwareFillOptions = {},
): Promise<boolean> {
  const document = store.document;
  if (document === null) return false;
  const target = store.activeRasterLayerId();
  if (target === null) return false;

  const { width, height } = document.size;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const { tx, ty } of buffer.tileCoords()) {
    minX = Math.min(minX, tx * TILE);
    minY = Math.min(minY, ty * TILE);
    maxX = Math.max(maxX, Math.min(width, (tx + 1) * TILE));
    maxY = Math.max(maxY, Math.min(height, (ty + 1) * TILE));
  }
  if (!Number.isFinite(minX)) return false; // 没有 stamp——笔画在画布外或被选区裁没了
  minX = Math.max(0, minX);
  minY = Math.max(0, minY);
  const bounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  if (bounds.width <= 0 || bounds.height <= 0) return false;

  const composite = compositeDocument(document, store.tiles);
  const mask = new Uint8Array(width * height);
  let any = false;
  for (let y = bounds.y; y < bounds.y + bounds.height; y++) {
    for (let x = bounds.x; x < bounds.x + bounds.width; x++) {
      if (buffer.alphaAt(x, y) > 0) {
        mask[y * width + x] = 255;
        any = true;
      }
    }
  }
  if (!any) return false;

  const filled = await contentAwareFill(composite.pixels, mask, width, height, options);

  // 覆盖度直接当选区调制用：笔刷边缘的软硬度天然带出羽化，
  // 不需要为修复画笔单独实现一套"选区裁剪"逻辑。
  const coverage = { valueAt: (x: number, y: number) => Math.round(buffer.alphaAt(x, y) * 255) };
  const transaction = store.beginPixelTransaction('污点修复画笔');
  const touched = applyFill(
    store.tiles,
    target,
    bounds,
    (x, y) => {
      const at = (y * width + x) * 4;
      return [filled[at]!, filled[at + 1]!, filled[at + 2]!, filled[at + 3]!];
    },
    transaction,
    { selection: coverage },
  );
  if (touched.length === 0) return false;
  store.commitPixels('污点修复画笔', transaction.end());
  store.invalidatePixels(touched);
  return true;
}
